import {
  useCheckout,
  PaymentElement,
  BillingAddressElement,
} from "@stripe/react-stripe-js/checkout";
import { useEffect, useState, type FC } from "react";
import Text from "../Text";
import { Button } from "../ui/button";
import { X } from "lucide-react";
import { Input } from "../ui/input";
import { Alert, AlertDescription, AlertTitle } from "../ui/alert";
import { toast } from "sonner";
import { triggerEvent } from "@/lib/utils";
import { useTranslation } from "react-i18next";

type Props = {
  onClose: () => void;
};

const CheckoutForm: FC<Props> = ({ onClose }) => {
  const checkoutState = useCheckout();
  const { t } = useTranslation();

  const [promoCode, setPromoCode] = useState("");
  const [isErrorPromoCode, setIsErrorPromoCode] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);

  useEffect(() => {
    if (firstLoad && checkoutState.type === "success") {
      const { checkout } = checkoutState;
      const { total, lineItems } = checkout;

      triggerEvent("Checkout Started", {
        plan_name: lineItems[0].name,
        price: total.total.amount,
      });

      setFirstLoad(false);
    }
  }, [firstLoad, checkoutState]);

  if (checkoutState.type === "loading") {
    return (
      <div className="w-[100%] h-[300px] flex items-center justify-center gap-[12px]">
        <img src="/svg/krunsh-icon.svg" style={{ zIndex: 2 }} />
        <img
          style={{
            animation: "loading-layout 1.3s",
          }}
          src="/svg/krunsh.svg"
        />
      </div>
    );
  }

  if (checkoutState.type === "error") {
    return <div>Error loading checkout: {checkoutState.error.message}</div>;
  }

  const { checkout } = checkoutState;

  const { lineItems, total, taxAmounts } = checkout;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const confirmResult = await checkout.confirm();

    if (confirmResult.type === "success") {
      triggerEvent("Checkout Completed", {
        plan_name: lineItems[0].name,
        price: total.total.amount,
      });
    }

    if (confirmResult.type === "error") {
      const { error } = confirmResult;
      if (error) {
        toast.error(error.message, {
          duration: 2000,
        });
        triggerEvent("Checkout Failed", {
          error_code: error.code,
        });
      }
    }
    setIsLoading(false);
  };

  let taxText = "N.C";

  if (total.taxInclusive.minorUnitsAmount) {
    taxText = total.taxInclusive.amount;
  }

  return (
    <div className="w-[100%]">
      <div className="flex justify-between mb-[32px] w-[100%]">
        <Text fontWeight={600} fontSize={20}>
          {`Plan ${lineItems[0].name}`}
        </Text>
        <X className="cursor-pointer" onClick={() => onClose()} />
      </div>
      <div className="flex gap-[24px] justify-between">
        <form
          onSubmit={handleSubmit}
          className="flex gap-[24px] w-[100%] flex-col sm:flex-row"
        >
          <BillingAddressElement />

          <PaymentElement />

          <div>
            <Text fontSize={14} fontWeight={600}>
              {t("promotion_code")}
            </Text>
            <div className="flex items-center gap-[12px] mt-[8px]">
              <Input
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder={t("promo_code")}
              />
              <Button
                disabled={promoCode.length === 0}
                type="button"
                onClick={() => {
                  checkout.applyPromotionCode(promoCode).then(({ type }) => {
                    if (type === "error") {
                      setIsErrorPromoCode(true);
                      triggerEvent("Promo Code Failed", {
                        code: promoCode,
                      });
                    }
                    if (type === "success") {
                      setIsErrorPromoCode(false);
                      toast.success("Code promo activé.", {
                        duration: 2000,
                      });
                      triggerEvent("Promo Code Applied", {
                        selected_plan: lineItems[0].name,
                        discount_value: total.discount.amount,
                        code: promoCode,
                      });
                    }
                  });
                }}
              >
                {t("apply")}
              </Button>
            </div>
            {isErrorPromoCode && (
              <Alert variant="destructive" className="mt-[12px]">
                <AlertTitle>{t("invalid_code_promo")}</AlertTitle>
                <AlertDescription>
                  <Text>{t("invalid_code_promo_description")}</Text>
                </AlertDescription>
              </Alert>
            )}

            <div className="bg-[#FCFCFD] w-[100%] sm:w-[368px] p-[16px] rounded-[12px] mt-[16px]">
              <Text fontSize={28} fontWeight={600}>
                {t("summary")}
              </Text>
              <div className="flex justify-between mt-[16px]">
                <Text fontSize={16} fontWeight={600}>
                  {lineItems[0].name}
                </Text>
                <Text fontSize={16} fontWeight={600}>
                  {/* {triggerPaymentAction === "creditPack"
                    ? `${(
                        (total.total.minorUnitsAmount -
                          total.taxInclusive.minorUnitsAmount) /
                        100
                      ).toFixed(2)} €`
                    : `20,82 €`} */}
                  {`${(
                    (total.total.minorUnitsAmount -
                      total.taxInclusive.minorUnitsAmount) /
                    100
                  ).toFixed(2)} €`}
                </Text>
              </div>

              <hr className="mt-[16px]" />

              <div className="flex justify-between mt-[16px]">
                <Text fontSize={16} fontWeight={600} className="uppercase">
                  {/* @ts-ignore */}
                  {`${t("vat")} (${taxAmounts?.[0]?.percentage ? `${taxAmounts[0].percentage}%` : "N.C"})`}
                </Text>
                <Text fontSize={16} fontWeight={600}>
                  {taxText}
                </Text>
              </div>

              {total.discount.minorUnitsAmount !== 0 && (
                <div className="flex justify-between mt-[8px]">
                  <Text fontSize={16} fontWeight={600} color="#22A06B">
                    {t("promo_code")}
                  </Text>
                  <Text fontSize={16} fontWeight={600} color="#22A06B">
                    {total.discount.amount}
                  </Text>
                </div>
              )}
              <hr className="mt-[16px]" />
              <div className="flex justify-between mt-[16px]">
                <Text fontSize={16} fontWeight={600}>
                  {t("due_today")}
                </Text>
                <Text fontSize={16} fontWeight={600}>
                  {total.total.amount}
                </Text>
              </div>
              <Button
                onClick={() => {
                  triggerEvent("Checkout Payment Clicked", {
                    selected_plan: lineItems[0].name,
                    has_promo: total.discount.minorUnitsAmount !== 0,
                  });
                }}
                className="w-full mt-[16px]"
                disabled={checkoutState.type !== "success" || isLoading}
                type="submit"
              >
                {t("pay_now")}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CheckoutForm;
